import { useEffect, useMemo, useState } from 'react';
import { Info, QrCode } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import { deleteTablet, useTablets } from '@/lib/api/tablet';
import { setEventKey, useEventKey } from '@/lib/api/tabletControl';
import { useTabletGroups } from '@/lib/api/tabletGroup';
import { Tablet } from '@/lib/schema/tablet';
import { TabletGroup } from '@/lib/schema/tabletGroup';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { toast } from 'sonner';
import { columns, TabletsTableMeta } from './columns';
import { TabletFormDialog } from './tabletFormDialog';
import { TabletQrDialog } from './tabletQrDialog';

// Global event key card: shared by every tablet, last segment of the QR payload.
function EventKeyCard() {
  const [eventKey, loading] = useEventKey();
  const [draft, setDraft] = useState(eventKey);
  const [saving, setSaving] = useState(false);

  // Follow remote changes until the user starts editing.
  useEffect(() => {
    setDraft(eventKey);
  }, [eventKey]);

  const trimmed = draft.trim();
  const dirty = trimmed !== eventKey;
  const valid = trimmed.length > 0 && !trimmed.includes('_');

  const save = async () => {
    if (!dirty || !valid) return;
    setSaving(true);
    try {
      await setEventKey(trimmed);
      toast.success('Event key updated — all QR codes now use it');
    } catch (error) {
      console.error('Error saving event key:', error);
      toast.error('Failed to save event key');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-6 flex flex-col gap-3 rounded-md border p-4 sm:flex-row sm:items-end">
      <div className="flex-1 space-y-1.5">
        <Label htmlFor="event-key" className="flex items-center gap-2">
          <QrCode className="h-4 w-4" />
          Event key (all tablets)
        </Label>
        <Input
          id="event-key"
          autoComplete="off"
          value={draft}
          disabled={loading}
          inError={!valid}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void save();
          }}
          className="max-w-xs font-mono"
        />
        <p className="text-sm text-muted-foreground">
          Each QR code is the CRC32 of Suite ID_Single event key_Event key.
          Changing the event key changes every code.
        </p>
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => setDraft(eventKey)}
          disabled={!dirty || saving}
        >
          Reset
        </Button>
        <Button
          type="button"
          onClick={save}
          disabled={!dirty || !valid || saving || loading}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

export function Tablets() {
  const [tablets] = useTablets();
  const [groups] = useTabletGroups();

  const [formOpen, setFormOpen] = useState(false);
  const [tabletToEdit, setTabletToEdit] = useState<Tablet | null>(null);
  const [tabletToDelete, setTabletToDelete] = useState<Tablet | null>(null);
  const [tabletForQr, setTabletForQr] = useState<Tablet | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [eventKey] = useEventKey();

  const groupsById = useMemo(
    () =>
      new Map<string, TabletGroup>(
        (groups || []).filter((g) => g.id).map((g) => [g.id!, g])
      ),
    [groups]
  );

  const handleDelete = async () => {
    if (!tabletToDelete?.id) return;

    setIsDeleting(true);
    try {
      await deleteTablet(tabletToDelete.id);
      toast.success('Tablet deleted');
      setTabletToDelete(null);
    } catch (error) {
      console.error('Error deleting tablet:', error);
      toast.error('Failed to delete tablet');
    } finally {
      setIsDeleting(false);
    }
  };

  const table = useReactTable({
    data: (tablets || []) as Tablet[],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      sorting: [{ id: 'name', desc: false }],
      pagination: { pageSize: 25 },
    },
    meta: {
      groupsById,
      onEdit: (tablet) => {
        setTabletToEdit(tablet);
        setFormOpen(true);
      },
      onDelete: setTabletToDelete,
      onShowQr: setTabletForQr,
    } satisfies TabletsTableMeta,
  });

  return (
    <div className="w-full pb-4">
      <Alert className="mb-6">
        <Info className="h-4 w-4" />
        <AlertTitle>Naming</AlertTitle>
        <AlertDescription>
          The tablet name must match the Vision Edge Luxury Suite name exactly
          — the tablets look themselves up in the suite feed by that name.
        </AlertDescription>
      </Alert>
      <EventKeyCard />
      <div className="flex justify-between items-center pb-6">
        <Input
          name="filter"
          autoComplete="off"
          placeholder="Search by name, location or suite ID..."
          onChange={(event) => {
            table.getColumn('name')?.setFilterValue(event.target.value);
          }}
          className="max-w-sm"
        />
        <Button
          type="button"
          onClick={() => {
            setTabletToEdit(null);
            setFormOpen(true);
          }}
        >
          New
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={header.id === 'actions' ? 'w-px' : ''}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end space-x-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          {table.getFilteredRowModel().rows.length} of{' '}
          {table.getPreFilteredRowModel().rows.length} tablet(s)
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      </div>

      <TabletFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        tablet={tabletToEdit}
      />

      <TabletQrDialog
        tablet={tabletForQr}
        eventKey={eventKey}
        onOpenChange={(open) => {
          if (!open) setTabletForQr(null);
        }}
      />

      <Dialog
        open={!!tabletToDelete}
        onOpenChange={(open) => {
          if (!open) setTabletToDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete tablet</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{tabletToDelete?.name}"? The
              suite XML feed will stop returning it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setTabletToDelete(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
